import React from "react";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { IndeterminateProgress } from "./IndeterminateProgress";
import { Input } from "../ui/input";
import { SpinnerIcon } from "./SpinnerIcon";
import { LanguageSelect } from "./LanguageSelect";
import type { ISO639_1_Language } from "@shared/types";
import { useDetectLanguage } from "../../hooks/useDetectLanguage";
import { toast } from "sonner";

const fileExtensions = ["txt", "docx", "pdf", "md"];
// biome-ignore lint/style/useTemplate: <accept text files>
const acceptedFileExtensions = "." + fileExtensions.join(",.");
const mbLimit = 10;
const fileSizeLimit = mbLimit * 1024 * 1024; // 10 MB

interface InputFormProps {
	actionTitle: string;
	text: string;
	isLoading: boolean;
	isReadingFile: boolean;
	language: ISO639_1_Language;
	minTextLength: number;
	maxTextLength: number;
	warningText: string;
	submitDisabled?: boolean;
	fileInputRef?: React.RefObject<HTMLInputElement | null>;
	onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onTextChange: (text: string) => void;
	onPressSubmit: () => void;
	onLanguageChange: (language: ISO639_1_Language) => void;
}

export const InputForm: React.FC<InputFormProps> = (props) => {
	const {
		text,
		isLoading,
		isReadingFile,
		actionTitle,
		language,
		minTextLength,
		maxTextLength,
		submitDisabled = false,
		warningText,
		fileInputRef,
		onLanguageChange,
		onTextChange,
		onFileChange,
		onPressSubmit,
	} = props;
	const detectLanguage = useDetectLanguage(onLanguageChange);
	const isSubmitDisabled =
		submitDisabled ||
		!text.trim() ||
		isLoading ||
		text.length < minTextLength ||
		text.length > maxTextLength;

	const handleBlur = React.useCallback(() => {
		detectLanguage(text);
	}, [text, detectLanguage]);

	const handlePaste = React.useCallback(
		(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
			const pastedText = event.clipboardData.getData("text");
			detectLanguage(text + pastedText);
		},
		[text, detectLanguage],
	);

	const handleFileChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			const fileSize = file?.size;
			if (fileSize && fileSize > fileSizeLimit) {
				toast.error("File is too large", {
					description: `Please upload a file smaller than ${mbLimit} MB`,
				});
				return;
			}
			const fileName = file?.name;
			const extension = fileName?.split(".").pop();
			if (extension && fileExtensions.includes(extension)) {
				onFileChange(event);
			} else {
				toast.error("Invalid file format", {
					description: "Only text files are supported",
				});
			}
		},
		[onFileChange],
	);

	return (
		<div className="h-full flex flex-col gap-3 px-3">
			<div className="flex-1 flex flex-col min-h-0">
				<div className="flex flex-shrink-0">
					<h2 className="text-base font-semibold tracking-tight flex-1">
						Paste your text
					</h2>

					<Button
						variant="outline"
						size="icon"
						className="size-6"
						disabled={!text.trim() || isLoading}
						onClick={() => onTextChange("")}
						aria-label="Clear text"
					>
						<XMarkIcon />
					</Button>
				</div>

				<div className="flex-1 mt-2 mb-2 flex flex-col min-h-0">
					<Textarea
						className="flex-1 resize-none overflow-auto"
						value={text}
						onChange={(e) => onTextChange(e.target.value)}
						placeholder="Type or paste your text here…"
						disabled={isLoading}
						maxLength={maxTextLength}
						minLength={minTextLength}
						onBlur={handleBlur}
						onPaste={handlePaste}
					/>
					<div className="flex items-start max-w-full gap-1">
						{text.length < minTextLength ? (
							<p className="text-sm text-muted-foreground text-left pt-2">
								At least {minTextLength} characters are required for reliable
								results.
							</p>
						) : (
							<div className="flex-1" />
						)}
						<p
							className={`text-sm text-right pt-2 flex-shrink-0 ml-auto ${
								text.length >= maxTextLength
									? "text-destructive"
									: "text-muted-foreground"
							}`}
						>
							{text.length} / {maxTextLength}
						</p>
					</div>
				</div>
			</div>

			<div className="flex flex-col flex-shrink-0">
				<h2 className="text-base font-semibold tracking-tight">
					Or upload a file
				</h2>

				<Input
					id="file-input"
					type="file"
					accept={acceptedFileExtensions}
					className="mt-2"
					ref={fileInputRef}
					onChange={handleFileChange}
					disabled={isReadingFile || isLoading}
				/>

				<div className="h-2 mt-1 flex items-center">
					{isReadingFile && <IndeterminateProgress />}
				</div>
			</div>
			<div className="flex gap-3 flex-shrink-0">
				<LanguageSelect
					value={language}
					defaultValue="en"
					onChange={onLanguageChange}
					disabled={isLoading}
				/>
				<Button
					className="flex-3"
					disabled={isSubmitDisabled}
					onClick={onPressSubmit}
				>
					{actionTitle}
					{isLoading ? <SpinnerIcon /> : <CheckCircleIcon />}
				</Button>
			</div>

			<div className="px-6 pb-6 pt-3 flex flex-col flex-shrink-0 items-center">
				<p className="w-4/5 text-xs text-muted-foreground text-center">
					{warningText}
				</p>
			</div>
		</div>
	);
};
